import { ref, onUnmounted, onMounted } from 'vue';

/**
 * Composable to simplify the usage of intersection observer for Composition API based components
 * @param callback function invoked on intersection of the element
 * @param options intersection observer options
 *
 * **usage-case 1**:
 * If params _callback_ and _options_ are given, then observer is automatically initialized in
 * _onMounted_ hook, and the returned function _initObserver_ has params _callback_ and _options_
 * bounded to the values passed to the composable
 *
 * **usage-case 2**:
 * Params _callback_ and _options_ are NOT given. Then, the function _initObserver_ has unbound params, which
 * have to be provided; the function _initObserver_ should be invoked externally for the observer
 * to be initialized
 */

export function initIntersectionObserver(
	callback: IntersectionObserverCallback | undefined = undefined,
	options?: IntersectionObserverInit
) {
	const observer = ref<IntersectionObserver>();
	let initObserver: typeof callback extends IntersectionObserverCallback ? () => void : typeof unboundObserver;

	function unboundObserver(callback: IntersectionObserverCallback, options: IntersectionObserverInit | undefined) {
		if (observer.value) {
			observer.value.disconnect();
		}

		observer.value = new IntersectionObserver(callback, options);
	}

	onUnmounted(() => {
		observer.value?.disconnect();
	});

	if (callback && options) {
		initObserver = unboundObserver.bind(null, callback, options);

		onMounted(() => {
			(initObserver as () => void)();
		});
	} else {
		initObserver = unboundObserver;
	}

	function addToObserver(htmlElement: Element) {
		observer.value?.observe(htmlElement);
	}

	function removeFromObserver(htmlElement: Element) {
		observer.value?.unobserve(htmlElement);
	}

	return {
		observer,
		initObserver,
		addToObserver,
		removeFromObserver,
	};
}
